// src/_utils/icsGenerator.js

/**
 * Escapa caratteri speciali per il formato ICS
 */
const escapeIcs = (str = "") => {
  if (!str) return "";
  return String(str)
    .replace(/\\/g, "\\\\")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;")
    .replace(/\n/g, "\\n");
};

/**
 * Normalizza un evento in formato standard
 * Supporta diversi formati di input
 */
const normalizeEvent = (event) => {
  let start, end, title, description, location;

  // CASO 1: startDate + startTime (CalXlsPage - eventCalendarList)
  if (event.startDate && event.startTime) {
    const startDateStr = event.startDate;
    const startTimeStr = event.startTime;
    const endDateStr = event.endDate || event.startDate;
    const endTimeStr = event.endTime || event.startTime;
    
    start = new Date(`${startDateStr}T${startTimeStr}:00`);
    end = new Date(`${endDateStr}T${endTimeStr}:00`);
    title = event.name || event.title || event.titolo || "Evento";
    description = event.description || event.note || "";
    location = event.location || event.luogo || "";
  }
  // CASO 2: start + end (Date objects - MonthlySportivi)
  else if (event.start instanceof Date && event.end instanceof Date) {
    start = event.start;
    end = event.end;
    title = event.titolo || event.title || event.name || "Evento";
    description = event.description || event.calendarDescription || event.note || "";
    location = event.luogo || event.location || "";
  }
  // CASO 3: stringhe date
  else if (event.start && event.end) {
    start = new Date(event.start);
    end = new Date(event.end);
    title = event.title || event.titolo || event.name || "Evento";
    description = event.description || event.note || "";
    location = event.location || event.luogo || "";
  }
  else {
    console.warn("Evento non riconosciuto:", event);
    return null;
  }

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    console.warn("Date non valide:", event);
    return null;
  }

  return { start, end, title, description, location };
};

/**
 * Formatta data per ICS: YYYYMMDDTHHMMSS
 */
const toIcsDateTime = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}00`;
};

/**
 * Formatta data per Google Calendar
 */
const formatForGoogle = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  return date.toISOString().replace(/-|:|\.\d+/g, '');
};

/**
 * Genera contenuto ICS per singolo evento
 */
export const generateIcsContent = (event) => {
  const normalized = normalizeEvent(event);
  if (!normalized) return "";

  const { start, end, title, description, location } = normalized;
  const now = new Date();
  const dtstamp = now.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  
  let lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//VoluntHub//Calendar//IT",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:event-${Date.now()}@volunthub`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART;TZID=Europe/Rome:${toIcsDateTime(start)}`,
    `DTEND;TZID=Europe/Rome:${toIcsDateTime(end)}`,
    "SEQUENCE:0",
    `SUMMARY:${escapeIcs(title)}`,
    `DESCRIPTION:${escapeIcs(description)}`
  ];

  if (location) {
    lines.push(`LOCATION:${escapeIcs(location)}`);
  }

  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\r\n");
};

/**
 * Genera contenuto ICS per MULTIPLI eventi
 */
export const generateMultipleIcsContent = (events) => {
  if (!events || events.length === 0) return "";
  
  const validEvents = events
    .map(e => normalizeEvent(e))
    .filter(e => e !== null);

  if (validEvents.length === 0) return "";

  const now = new Date();
  const dtstamp = now.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//VoluntHub//Calendar//IT",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH"
  ];

  validEvents.forEach((ev, i) => {
    lines.push(
      "BEGIN:VEVENT",
      `UID:event-${Date.now()}-${i}@volunthub`,
      `DTSTAMP:${dtstamp}`,
      `DTSTART;TZID=Europe/Rome:${toIcsDateTime(ev.start)}`,
      `DTEND;TZID=Europe/Rome:${toIcsDateTime(ev.end)}`,
      "SEQUENCE:0",
      `SUMMARY:${escapeIcs(ev.title)}`,
      `DESCRIPTION:${escapeIcs(ev.description)}`
    );
    if (ev.location) {
      lines.push(`LOCATION:${escapeIcs(ev.location)}`);
    }
    lines.push("END:VEVENT");
  });

  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
};

/**
 * Scarica file ICS
 */
export const downloadIcsFile = (icsContent, filename = "evento.ics") => {
  if (!icsContent) {
    console.warn("Nessun contenuto ICS");
    return;
  }

  if (!filename.endsWith('.ics')) {
    filename += '.ics';
  }

  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const blobUrl = window.URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);
};

/**
 * Ottieni link Google Calendar
 */
export const getGoogleCalendarLink = (event) => {
  const normalized = normalizeEvent(event);
  if (!normalized) return "";

  const { start, end, title, description, location } = normalized;
  const baseUrl = "https://calendar.google.com/calendar/render?action=TEMPLATE";
  
  const params = new URLSearchParams({
    text: title,
    dates: `${formatForGoogle(start)}/${formatForGoogle(end)}`,
    details: description || "",
    location: location || ""
  });

  return `${baseUrl}&${params.toString()}`;
};

/**
 * Apri Google Calendar
 */
export const openGoogleCalendar = (event) => {
  const link = getGoogleCalendarLink(event);
  if (link) {
    window.open(link, '_blank');
  }
};

// ============================================
// ALIAS PER COMPATIBILITÀ CON I COMPONENTI
// ============================================

/**
 * Alias per generateIcsContent (singolo evento)
 */
export const generateIcs = generateIcsContent;

/**
 * Alias per generateMultipleIcsContent (multipli eventi)
 */
export const generateMultipleIcs = generateMultipleIcsContent;

/**
 * Alias per downloadIcsFile
 */
export const downloadIcs = downloadIcsFile;

/**
 * Scarica singolo evento (comodo per uso diretto)
 */
export const downloadEventIcs = (event, filename) => {
  const content = generateIcsContent(event);
  if (content) {
    downloadIcsFile(content, filename);
  }
};

/**
 * Scarica multipli eventi (comodo per uso diretto)
 */
export const downloadEventsIcs = (events, filename) => {
  const content = generateMultipleIcsContent(events);
  if (content) {
    downloadIcsFile(content, filename);
  }
};